import { computed, ref, watch, type InjectionKey, type Ref } from "vue";
import * as defaultAPI from "@/api/admin/admin";
import { useImpersonationStore } from "@/modules/auth/stores/impersonation";
import type {
  AdminV1WaffleStatusRetrieve200Flags,
  AdminV1WaffleStatusRetrieve200Switches,
  AdminV1WaffleStatusRetrieve200Samples,
  AdminV1WaffleStatusRetrieve200,
  AdminV1WaffleStatusRetrieveParams,
} from "@/api/model";
import { captureException } from "@sentry/vue";

interface UseWaffle {
  switches: Ref<AdminV1WaffleStatusRetrieve200Switches>;
  flags: Ref<AdminV1WaffleStatusRetrieve200Flags>;
  samples: Ref<AdminV1WaffleStatusRetrieve200Samples>;
  isActive: (name: string) => boolean;
  fetchStatus: () => Promise<void>;
}

export interface WaffleAPI {
  adminV1WaffleStatusRetrieve: (
    params?: AdminV1WaffleStatusRetrieveParams
  ) => Promise<AdminV1WaffleStatusRetrieve200>;
}

/**
 * Lets a parent that has already awaited fetchStatus() share its instance with
 * descendants, so flag-dependent layout does not reflow on a second fetch.
 */
export const WaffleKey: InjectionKey<UseWaffle> = Symbol("waffle");

export const useWaffle = (api: WaffleAPI = defaultAPI): UseWaffle => {
  const impersonation = useImpersonationStore();
  const switches = ref<AdminV1WaffleStatusRetrieve200Switches>({});
  const flags = ref<AdminV1WaffleStatusRetrieve200Flags>({});
  const samples = ref<AdminV1WaffleStatusRetrieve200Samples>({});
  const isActive = (name: string): boolean => {
    return Boolean(
      flags.value[name]?.is_active ||
        switches.value[name]?.is_active ||
        samples.value[name]?.is_active
    );
  };
  const scopedOrganizationId = computed(() =>
    impersonation.isImpersonating && impersonation.impersonatedOrg
      ? impersonation.impersonatedOrg.id
      : undefined
  );
  const impersonatedOrganizationParams = ():
    | AdminV1WaffleStatusRetrieveParams
    | undefined =>
    scopedOrganizationId.value === undefined
      ? undefined
      : { organization: scopedOrganizationId.value };
  const fetchStatus = async (): Promise<void> => {
    return api
      .adminV1WaffleStatusRetrieve(impersonatedOrganizationParams())
      .then((response) => {
        flags.value = response.flags || {};
        samples.value = response.samples || {};
        switches.value = response.switches || {};
      })
      .catch((error) => {
        captureException(error, {
          extra: { message: "Was unable to fetch waffle status" },
        });
        console.error("Failed to fetch waffle status:", error);
      });
  };

  watch(scopedOrganizationId, () => {
    fetchStatus();
  });

  return {
    flags,
    switches,
    samples,
    isActive,
    fetchStatus,
  };
};
